import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
const SoundPlayerContext = createContext(undefined);
export function SoundPlayerProvider({ children }) {
    // Legacy single-sound state
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolumeState] = useState(0.7);
    const [legacyAudio, setLegacyAudio] = useState(null);
    // Multi-sound state - metadata only (no audioElement)
    const [activeSounds, setActiveSounds] = useState(new Map());
    const [globalVolume, setGlobalVolumeState] = useState(70); // 0-100
    const [isPlayingGlobally, setIsPlayingGlobally] = useState(false);
    // Audio element references stored separately (not in state)
    const audioElementsRef = useRef(new Map());
    const wakeLockRef = useRef(null);
    // Apply global volume to all active sounds
    useEffect(() => {
        activeSounds.forEach((activeSound) => {
            const audioElement = audioElementsRef.current.get(activeSound.id);
            if (audioElement) {
                audioElement.volume = (activeSound.currentVolume / 100) * (globalVolume / 100);
            }
        });
    }, [globalVolume, activeSounds]);
    // Cleanup audio elements on unmount
    useEffect(() => {
        return () => {
            audioElementsRef.current.forEach((audioElement) => {
                audioElement.pause();
                audioElement.src = '';
            });
            audioElementsRef.current.clear();
            if (legacyAudio) {
                legacyAudio.pause();
                legacyAudio.src = '';
            }
            // Release wake lock on unmount
            if (wakeLockRef.current) {
                wakeLockRef.current
                    .release()
                    .catch((err) => console.warn('Wake lock release on unmount failed:', err));
            }
        };
    }, [legacyAudio]);
    // Update MediaSession for background audio and mobile controls
    useEffect(() => {
        if (!('mediaSession' in navigator) || activeSounds.size === 0) {
            return;
        }
        try {
            const firstSound = Array.from(activeSounds.values())[0];
            // Set metadata for lock screen and notification center
            navigator.mediaSession.metadata = new MediaMetadata({
                title: firstSound.title,
                artist: firstSound.description || 'Sound Studio',
                album: 'Soundscape',
                artwork: [
                    {
                        src: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 96 96%22%3E%3Ccircle cx=%2248%22 cy=%2248%22 r=%2248%22 fill=%22%23EC4899%22/%3E%3Cpath d=%22M48 32c-8.8 0-16 7.2-16 16v12c0 8.8 7.2 16 16 16s16-7.2 16-16v-12c0-8.8-7.2-16-16-16z%22 fill=%22white%22/%3E%3C/svg%3E',
                        sizes: '96x96',
                        type: 'image/svg+xml',
                    },
                ],
            });
            // Set up action handlers for media controls
            navigator.mediaSession.setActionHandler('play', () => {
                // Resume all non-paused sounds
                audioElementsRef.current.forEach((audio) => {
                    const soundState = Array.from(activeSounds.values()).find(s => audioElementsRef.current.get(s.id) === audio);
                    if (soundState && !soundState.isPaused) {
                        audio.play().catch(e => console.error('Play error:', e));
                    }
                });
            });
            navigator.mediaSession.setActionHandler('pause', () => {
                // Pause all active sounds
                audioElementsRef.current.forEach((audio) => {
                    audio.pause();
                });
            });
            // Update playback state
            if (isPlayingGlobally) {
                navigator.mediaSession.playbackState = 'playing';
            }
            else {
                navigator.mediaSession.playbackState = 'paused';
            }
        }
        catch (error) {
            console.error('MediaSession setup error:', error);
        }
    }, [activeSounds, isPlayingGlobally]);
    // Handle visibility change to keep audio playing in background
    useEffect(() => {
        const handleVisibilityChange = () => {
            // When app comes back into focus, resume audio if it should be playing
            if (!document.hidden && isPlayingGlobally) {
                audioElementsRef.current.forEach((audio) => {
                    // Only resume if it's not paused
                    const soundState = Array.from(activeSounds.values()).find(s => audioElementsRef.current.get(s.id) === audio && !s.isPaused);
                    if (soundState && audio.paused) {
                        audio.play().catch(e => {
                            console.error('Resume audio error:', e);
                            // Try with a small delay if there's an error
                            setTimeout(() => {
                                audio.play().catch(e2 => console.error('Delayed resume failed:', e2));
                            }, 100);
                        });
                    }
                });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isPlayingGlobally, activeSounds]);
    // Legacy single-sound methods
    const ensureAudio = useCallback(() => {
        if (!legacyAudio) {
            const audio = new Audio();
            audio.loop = true;
            audio.volume = volume;
            setLegacyAudio(audio);
            return audio;
        }
        return legacyAudio;
    }, [legacyAudio, volume]);
    const play = useCallback((track) => {
        const audio = ensureAudio();
        if (currentTrack && currentTrack.id === track.id && isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }
        setCurrentTrack(track);
        audio.src = track.url;
        audio.volume = volume;
        audio
            .play()
            .then(() => {
            setIsPlaying(true);
        })
            .catch((err) => {
            console.error('Audio playback error', err);
        });
    }, [currentTrack, isPlaying, volume, ensureAudio]);
    const pause = useCallback(() => {
        if (!legacyAudio)
            return;
        legacyAudio.pause();
        setIsPlaying(false);
    }, [legacyAudio]);
    const resume = useCallback(() => {
        if (!legacyAudio || !currentTrack)
            return;
        legacyAudio
            .play()
            .then(() => setIsPlaying(true))
            .catch((err) => console.error('Audio resume error', err));
    }, [legacyAudio, currentTrack]);
    const stop = useCallback(() => {
        if (!legacyAudio)
            return;
        legacyAudio.pause();
        legacyAudio.currentTime = 0;
        setIsPlaying(false);
        setCurrentTrack(null);
    }, [legacyAudio]);
    const setVolume = useCallback((next) => {
        const clamped = Math.min(1, Math.max(0, next));
        setVolumeState(clamped);
        if (legacyAudio) {
            legacyAudio.volume = clamped;
        }
    }, [legacyAudio]);
    // Multi-sound methods
    const playMultiSound = useCallback(async (track, initialVolume = 70) => {
        // If sound is already playing, just return
        if (audioElementsRef.current.has(track.id)) {
            console.log(`Sound ${track.id} is already playing`);
            return;
        }
        try {
            console.log(`Creating audio element for ${track.id}, URL: ${track.url}`);
            const audio = new Audio(track.url);
            audio.crossOrigin = 'anonymous'; // Enable cross-origin audio loading
            audio.loop = true;
            audio.volume = (initialVolume / 100) * (globalVolume / 100);
            // Store audio element reference before playing
            audioElementsRef.current.set(track.id, audio);
            console.log(`Playing ${track.id} with volume ${audio.volume}`);
            await audio.play();
            console.log(`${track.id} started playing successfully`);
            // Request wake lock to keep device awake during playback
            if ('wakeLock' in navigator && !wakeLockRef.current) {
                try {
                    const wakeLock = await navigator.wakeLock.request('screen');
                    wakeLockRef.current = wakeLock;
                    console.log('Wake lock acquired for background audio playback');
                }
                catch (err) {
                    console.warn('Wake lock request failed:', err);
                }
            }
            // Update state with metadata only
            setActiveSounds((prev) => {
                const newMap = new Map(prev);
                newMap.set(track.id, {
                    ...track,
                    currentVolume: initialVolume,
                    isPaused: false,
                });
                console.log(`Active sounds count: ${newMap.size}`);
                return newMap;
            });
            setIsPlayingGlobally(true);
        }
        catch (error) {
            console.error('Multi-sound playback error:', error);
            audioElementsRef.current.delete(track.id);
            throw error;
        }
    }, [globalVolume]);
    const pauseMultiSound = useCallback((soundId) => {
        const audioElement = audioElementsRef.current.get(soundId);
        if (audioElement) {
            audioElement.pause();
            setActiveSounds((prev) => {
                const newMap = new Map(prev);
                const sound = newMap.get(soundId);
                if (sound) {
                    sound.isPaused = true;
                    newMap.set(soundId, { ...sound });
                }
                return newMap;
            });
        }
    }, []);
    const resumeMultiSound = useCallback((soundId) => {
        const audioElement = audioElementsRef.current.get(soundId);
        if (audioElement) {
            audioElement.play();
            setActiveSounds((prev) => {
                const newMap = new Map(prev);
                const sound = newMap.get(soundId);
                if (sound) {
                    sound.isPaused = false;
                    newMap.set(soundId, { ...sound });
                }
                return newMap;
            });
        }
    }, []);
    const stopMultiSound = useCallback((soundId) => {
        const audioElement = audioElementsRef.current.get(soundId);
        if (audioElement) {
            audioElement.pause();
            audioElement.src = '';
        }
        audioElementsRef.current.delete(soundId);
        setActiveSounds((prev) => {
            const newMap = new Map(prev);
            newMap.delete(soundId);
            return newMap;
        });
    }, []);
    const stopAllSounds = useCallback(() => {
        audioElementsRef.current.forEach((audioElement) => {
            audioElement.pause();
            audioElement.src = '';
        });
        audioElementsRef.current.clear();
        setActiveSounds(new Map());
        setIsPlayingGlobally(false);
        // Release wake lock when stopping all sounds
        if (wakeLockRef.current) {
            wakeLockRef.current
                .release()
                .then(() => {
                wakeLockRef.current = null;
                console.log('Wake lock released');
            })
                .catch((err) => console.warn('Wake lock release failed:', err));
        }
    }, []);
    const setGlobalVolume = useCallback((vol) => {
        const clamped = Math.min(100, Math.max(0, vol));
        setGlobalVolumeState(clamped);
    }, []);
    const setSoundVolume = useCallback((soundId, vol) => {
        const clamped = Math.min(100, Math.max(0, vol));
        const audioElement = audioElementsRef.current.get(soundId);
        if (audioElement) {
            audioElement.volume = (clamped / 100) * (globalVolume / 100);
        }
        setActiveSounds((prev) => {
            const newMap = new Map(prev);
            const activeSound = newMap.get(soundId);
            if (activeSound) {
                activeSound.currentVolume = clamped;
                newMap.set(soundId, { ...activeSound });
            }
            return newMap;
        });
    }, [globalVolume]);
    const toggleGlobalPlayPause = useCallback(() => {
        if (audioElementsRef.current.size === 0)
            return;
        if (isPlayingGlobally) {
            activeSounds.forEach((activeSound) => {
                if (!activeSound.isPaused) {
                    const audioElement = audioElementsRef.current.get(activeSound.id);
                    if (audioElement) {
                        audioElement.pause();
                    }
                }
            });
            setIsPlayingGlobally(false);
        }
        else {
            activeSounds.forEach((activeSound) => {
                if (!activeSound.isPaused) {
                    const audioElement = audioElementsRef.current.get(activeSound.id);
                    if (audioElement) {
                        audioElement.play();
                    }
                }
            });
            setIsPlayingGlobally(true);
        }
    }, [activeSounds, isPlayingGlobally]);
    const value = {
        // Legacy
        currentTrack,
        isPlaying,
        volume,
        play,
        pause,
        resume,
        stop,
        setVolume,
        // Multi-sound
        activeSounds,
        globalVolume,
        isPlayingGlobally,
        playMultiSound,
        pauseMultiSound,
        resumeMultiSound,
        stopMultiSound,
        stopAllSounds,
        setGlobalVolume,
        setSoundVolume,
        toggleGlobalPlayPause,
    };
    return _jsx(SoundPlayerContext.Provider, { value: value, children: children });
}
export function useSoundPlayer() {
    const ctx = useContext(SoundPlayerContext);
    if (!ctx) {
        throw new Error('useSoundPlayer must be used within a SoundPlayerProvider');
    }
    return ctx;
}
